/**
 * Public URL helper (lumora#1488, #1489). One place that decides whether a public page link is a real
 * path (`/about`) or a hash route (`#/about`), so links never hard-code `#/`.
 *
 * Staff and Hub routes always stay hash routes; `isPublicRoute` is the single gate and the staff
 * deny list below is checked first so a misconfigured public list can never move the Hub.
 */
export function normalizeRoutingMode(raw) {
    return raw === "auto" || raw === "path" || raw === "hash" ? raw : "hash";
}
/** First path segments that are staff / Hub routes in every tenant. Never public, never rewritten. */
export const STAFF_ROUTE_PREFIXES = [
    "/dashboard",
    "/tasks",
    "/inspections",
    "/repair-orders",
    "/owner",
    "/platform",
    "/signin",
    "/orders",
    "/admin",
    "/bookings",
    "/__",
];
/** Public order-status tickets (`/o/:token`, printed in QR codes) are always public. */
export const ORDER_STATUS_PREFIX = "/o/";
const state = {
    mode: "hash",
    publicRoutes: [],
    staffRoutes: [],
    base: "",
};
function cleanBase(base) {
    const b = (base ?? "").trim().replace(/\/+$/, "");
    return b === "" || b.startsWith("/") ? b : `/${b}`;
}
/** Called once by the site shell after the routing mode has been resolved. */
export function configurePublicRouting(opts) {
    state.mode = opts.mode;
    state.publicRoutes = opts.publicRoutes;
    state.staffRoutes = opts.staffRoutes ?? [];
    state.base = cleanBase(opts.base);
}
export function activeRoutingMode() {
    return state.mode;
}
function pathOf(route) {
    const cut = route.search(/[?#]/);
    return cut < 0 ? route : route.slice(0, cut);
}
function matchesStaff(p) {
    return STAFF_ROUTE_PREFIXES.some((s) => p === s || p.startsWith(`${s}/`));
}
/** True for pages a visitor or crawler may reach by URL; false for staff, Hub and unknown routes. */
export function isPublicRoute(route, publicRoutes = state.publicRoutes) {
    const p = pathOf(route);
    if (p === "" || matchesStaff(p))
        return false;
    if (state.staffRoutes.some((s) => p === s || p.startsWith(`${s}/`)))
        return false;
    if (p.startsWith(ORDER_STATUS_PREFIX))
        return true;
    return publicRoutes.some((r) => (r === "/" ? p === "/" : p === r || p.startsWith(`${r}/`)));
}
/**
 * `publicUrl("/about", { mode: "path" })` is `/about`; `{ mode: "hash" }` is `#/about`, or
 * `https://host/#/about` when `origin` is given. Query and section parts ride along untouched.
 */
export function publicUrl(route, opts = {}) {
    const mode = opts.mode ?? state.mode;
    const origin = (opts.origin ?? "").replace(/\/+$/, "");
    const r = route.startsWith("/") ? route : `/${route}`;
    if (mode === "path") {
        const base = cleanBase(opts.base ?? state.base);
        return r === "/" ? `${origin}${base}/` : `${origin}${base}${r}`;
    }
    return origin ? `${origin}/#${r}` : `#${r}`;
}
/** Link target for any in-site route: public routes follow the mode, everything else stays a hash route. */
export function siteHref(route, opts = {}) {
    return isPublicRoute(route) ? publicUrl(route, opts) : publicUrl(route, { ...opts, mode: "hash" });
}
/**
 * Config-authored link target. A legacy `#/route` (written in a site config as a hash link) follows the
 * routing mode when it is a public page; every other value (tel:, https:, `#section`, staff hash) is returned as is.
 */
export function localHref(href) {
    return href.startsWith("#/") ? siteHref(href.slice(1)) : href;
}
//# sourceMappingURL=publicRouting.js.map